// Message catalogues (document 15, section 5.1): data only; web, mobile and api bring i18next.
import { ar } from './ar.js';
import { en } from './en.js';

export { NAV_AR } from './nav.js';
export { en, ar };
export type { Messages } from './en.js';

export type Language = 'en' | 'ar';
export const LANGUAGES: readonly Language[] = ['en', 'ar'];

/** i18next resources: one `translation` namespace per language. */
export const resources = { en: { translation: en }, ar: { translation: ar } } as const;

/** The writing direction of a language. */
export const dirOf = (lang: Language): 'ltr' | 'rtl' => (lang === 'ar' ? 'rtl' : 'ltr');

/** English plural suffixes and the CLDR forms Arabic needs for each. */
const EN_PLURALS = ['_one', '_other'];
const AR_PLURALS = ['_zero', '_one', '_two', '_few', '_many', '_other'];

type Tree = { [k: string]: string | Tree };

function flatten(t: Tree, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(t)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string') out[key] = v;
    else Object.assign(out, flatten(v, key));
  }
  return out;
}

/**
 * What the Arabic catalogue lacks against the English one: every plain key, and all six plural
 * forms for every English plural. The test suite and CI fail on anything listed.
 */
export function missingArabic(enTree: Tree = en, arTree: Tree = ar as unknown as Tree): string[] {
  const e = flatten(enTree);
  const a = flatten(arTree);
  const missing = new Set<string>();
  for (const key of Object.keys(e)) {
    const plural = EN_PLURALS.find((s) => key.endsWith(s));
    if (plural) {
      const base = key.slice(0, -plural.length);
      for (const s of AR_PLURALS) if (!(`${base}${s}` in a)) missing.add(`${base}${s}`);
    } else if (!(key in a)) missing.add(key);
  }
  return [...missing].sort();
}

/** Placeholders ({{name}}) a message uses. */
export const placeholders = (s: string): string[] =>
  [...s.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map((m) => m[1]!).sort();

/**
 * A pseudo-locale for development (`en-XA`): accented and about 40% longer, so clipped layouts and
 * strings that skip the catalogue stand out. Placeholders are kept as they are.
 */
export function pseudo(s: string): string {
  const map: Record<string, string> = {
    a: 'á',
    e: 'é',
    i: 'í',
    o: 'ó',
    u: 'ú',
    A: 'Á',
    E: 'É',
    O: 'Ó',
  };
  let out = '';
  let inside = false;
  for (let i = 0; i < s.length; i++) {
    if (s.startsWith('{{', i)) inside = true;
    if (s.startsWith('}}', i)) inside = false;
    out += inside ? s[i] : (map[s[i]!] ?? s[i]);
  }
  return `[${out}${'·'.repeat(Math.ceil(s.length * 0.4))}]`;
}

export function pseudoTree(t: Tree): Tree {
  return Object.fromEntries(
    Object.entries(t).map(([k, v]) => [k, typeof v === 'string' ? pseudo(v) : pseudoTree(v)]),
  );
}
